import { findContactByNumber } from "./lineContacts";

// localStorage key for per-number suppression of the post-call "remember this
// language" prompt. Maps normalized external-number -> epoch ms of the last
// "Not now" (or "Keep"). A prompt is suppressed for 30 days after that.
export const RECALL_SUPPRESS_KEY = "truva_recall_prompt_suppress";
const SUPPRESS_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * Decide whether (and how) to prompt the user to remember a translation
 * language for `number` after a translated call used `lang`, given the
 * current line's saved `contacts`.
 *
 * Returns null when there is nothing sensible to offer:
 *   - contact already saved with the exact language used, or
 *   - a suppressed number, or
 *   - missing number/lang.
 *
 * Otherwise one of:
 *   { kind: 'save',   number, lang, name }   - unsaved number
 *   { kind: 'always', number, lang, contactId, name } - saved, no language yet
 *   { kind: 'keep',   number, lang, usedLang, contactId, name } - saved but the
 *     saved lang differs from what this call used (we must NOT switch silently)
 *
 * `lang`/`usedLang` are the codes displayed via TRANSLATE_LANGUAGES.
 */
export function buildTranslateRecall(number, lang, contacts) {
  if (!number || !lang || !String(number).replace(/\D/g, "")) return null;
  const contact = findContactByNumber(contacts, number);
  const savedLang = (contact && contact.translate_lang) || "";
  const name = (contact && (contact.first_name || contact.number)) || "";
  if (!contact) {
    return { kind: "save", number, lang, name: "" };
  }
  if (!savedLang) {
    return { kind: "always", number, lang, contactId: contact.id, name };
  }
  if (savedLang === lang) return null;
  return {
    kind: "keep",
    number,
    lang: savedLang,
    usedLang: lang,
    contactId: contact.id,
    name,
  };
}

export function shouldSuppressRecall(number) {
  if (!number) return false;
  try {
    const map = JSON.parse(localStorage.getItem(RECALL_SUPPRESS_KEY) || "{}") || {};
    const at = map[number];
    return !!at && Date.now() - at < SUPPRESS_WINDOW_MS;
  } catch (e) {
    return false;
  }
}

export function rememberSuppressedRecall(number) {
  if (!number) return;
  try {
    const map = JSON.parse(localStorage.getItem(RECALL_SUPPRESS_KEY) || "{}") || {};
    map[number] = Date.now();
    localStorage.setItem(RECALL_SUPPRESS_KEY, JSON.stringify(map));
  } catch (e) {
    /* ignore storage errors (private mode etc.) */
  }
}

// Exposed for tests; kept out of the public surface above.
export const _SUPPRESS_WINDOW_MS = SUPPRESS_WINDOW_MS;
